// Q-DASH (2), HER ORDER: "a TRUTH tab in her owner dashboard: ... Approvals (applications, invite codes; move or mirror
// Advisor Applications), Financials (money in, house share, advisor payouts pending/sent/canceled with her cancel control,
// Amara's earnings, free credit given, disputes)", and Analytics ("visitors, sign-ups, readings chat/call, minutes,
// visit-to-first-reading conversion, advisors online, repeat clients, Amara's readings and minutes; by day, week, month").
//
// Every number is read at runtime from /api/owner/command/truth, an owner-only door; nothing about the Truth wing's
// tables or paths is in this file, because READER-MARKET-1 measures the shipped bundle. Approvals is a MIRROR: Approve,
// Re-send and Decline stay on the Truth console, which the button opens. A read that failed says so, never a zero.
import { useCallback, useEffect, useRef, useState } from "react";
import { loadOwnerData, ownerLoadNote } from "../lib/owner-load";
import { beginRead, readMayPaint } from "../lib/latest-read";

type Payout = { sessionId: string | number; advisor: string | null; cents: number; state: string; availableAt: string | null; sentAt: string | null; cancelledAt: string | null; cancelledReason: string | null; reversedAt?: string | null; sending?: boolean };
type Financials = {
  moneyIn: { cardCents: number; topUpCents: number; topUps: number; totalCents: number };
  houseShareCents: number; advisorShareCents: number;
  amara: { cents: number; readings: number };
  freeCredit: { cents: number; grants: number };
  payouts: { pending: { n: number; cents: number }; sent: { n: number; cents: number }; cancelled: { n: number; cents: number }; rows: Payout[] };
  disputes: { n: number; cents: number; unsent: number };
};
type Row = { period: string; visitors: number; signups: number; chat: number; calls: number; minutes: number; amaraReadings: number; amaraMinutes: number };
type Analytics = { bucket: string; series: Row[]; visitsSince: string | null;
  totals: Row & { firstReadings: number; repeatClients: number; clients: number; advisorsOnline: number } };
type Account = { id: number; email: string; name: string; balanceCents: number };
type Gift = { id: number; cents: number; email: string; createdAt: string; by: string; note: string };
type Application = { id: string | number; name: string; email: string; status: string; createdAt: string; inviteSentAt: string | null; inviteUsedAt: string | null };
type Approvals = { applications: Application[] };

export const TRUTH_PARTS = [
  { key: "analytics", label: "Analytics" },
  { key: "financials", label: "Financials" },
  { key: "approvals", label: "Approvals" },
  { key: "gifts", label: "Free minutes" },
] as const;
type Part = (typeof TRUTH_PARTS)[number]["key"];

export const TRUTH_BUCKETS = [{ key: "day", label: "By day" }, { key: "week", label: "By week" }, { key: "month", label: "By month" }] as const;
type Bucket = (typeof TRUTH_BUCKETS)[number]["key"];
/** Visit to first reading, as a percentage of visitors; a period with no visitors has no rate rather than 0%. */
export const truthRate = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 1000) / 10}%` : "—");

/** "$12.50" -> 1250; anything that is not a plain dollar amount above zero is null. */
export const giftCents = (text: string): number | null => {
  const m = /^\s*\$?\s*(\d{1,4})(?:\.(\d{1,2}))?\s*$/.exec(String(text || ""));
  if (!m) return null;
  const cents = Number(m[1]) * 100 + Number((m[2] || "0").padEnd(2, "0"));
  return cents > 0 ? cents : null;
};
/** Whole minutes a gift buys at one per-minute price. */
export const giftMinutes = (cents: number, rate: number) => (rate > 0 ? Math.floor(cents / rate) : 0);
const newRef = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `g${Date.now()}${Math.random().toString(36).slice(2, 10)}`);

/** Applications open in the Truth console through this owner door (it answers anybody else 404). */
export const TRUTH_APPLICATIONS_OPEN = "/api/owner/command/applications/open";

export const truthMoney = (cents: number | null | undefined) =>
  cents === null || cents === undefined || !Number.isFinite(Number(cents)) ? "—" : `$${(Number(cents) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (d: string | null | undefined) => {
  if (!d) return "";
  const t = new Date(d);
  return Number.isFinite(t.getTime()) ? t.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
};
// A payout claimed for sending in the last ten minutes is mid-transfer: the server refuses a cancel then, so none is offered.
const payoutState = (p: Payout) => (p.state === "cancelled" ? "Canceled" : p.state === "sent" ? "Sent" : p.state === "reversed" ? "Reversed" : p.sending ? "Being sent" : "Pending");

export function OwnerTruth({ rangeQuery, onDenied }: { rangeQuery: string; onDenied: () => void }) {
  const [part, setPart] = useState<Part>("analytics");
  const [bucket, setBucket] = useState<Bucket>("day");
  const [ana, setAna] = useState<Analytics | null>(null);
  const [gifts, setGifts] = useState<Gift[] | null>(null);
  const [q, setQ] = useState("");
  const [found, setFound] = useState<Account[] | null>(null);
  const [rates, setRates] = useState<number[]>([]);
  const [who, setWho] = useState<Account | null>(null);
  const [amount, setAmount] = useState("");
  const [giftWhy, setGiftWhy] = useState("");
  const [ref, setRef] = useState(newRef);
  const [fin, setFin] = useState<Financials | null>(null);
  const [apps, setApps] = useState<Approvals | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const readSeq = useRef(0);
  const load = useCallback(() => {
    const seq = beginRead(readSeq);
    loadOwnerData<Financials & Approvals & Analytics>(`/api/owner/command/truth?view=${part}&bucket=${bucket}&${rangeQuery}`).then((answer) => {
      if (!readMayPaint(readSeq, seq)) return;
      if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
      if (answer.ok) { if (part === "financials") setFin(answer.data); else if (part === "analytics") setAna(answer.data); else if (part === "gifts") setGifts((answer.data as unknown as { gifts: Gift[] }).gifts); else setApps(answer.data); }
      setNote(ownerLoadNote(answer));
    });
  }, [part, bucket, rangeQuery, onDenied]);
  useEffect(() => { load(); }, [load]);

  const cancel = async (p: Payout) => {
    if (!window.confirm(`Cancel ${p.advisor || "this advisor"}'s payout of ${truthMoney(p.cents)} for reading ${p.sessionId}? Nothing will be sent.`)) return;
    setBusy(String(p.sessionId));
    const res = await fetch("/api/owner/command/truth/payout-cancel", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: Number(p.sessionId) }),
    }).then((r) => r.json()).catch(() => null);
    setBusy("");
    setNote(res?.message || "That payout could not be canceled just now.");
    load();
  };

  const search = async () => {
    const res = await fetch(`/api/owner/command/truth/accounts?q=${encodeURIComponent(q)}`, { cache: "no-store" }).then((r) => r.json()).catch(() => null);
    if (!res?.ok) { setNote(res?.message || "Accounts could not be searched just now."); return; }
    setFound(res.accounts); setRates(res.rates || []); setWho(null);
  };
  const cents = giftCents(amount);
  const give = async () => {
    if (!who || !cents) return;
    if (!window.confirm(`Give ${truthMoney(cents)} of free credit to ${who.email}? It lands on her balance now.`)) return;
    setBusy("gift");
    const res = await fetch("/api/owner/command/truth/gift", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: who.id, cents, note: giftWhy, ref }),
    }).then((r) => r.json()).catch(() => null);
    setBusy("");
    setNote(res?.message || "The gift could not be given just now; nothing changed.");
    if (res?.ok) { setRef(newRef()); setAmount(""); setGiftWhy(""); setWho(null); setFound(null); load(); }
  };

  return (
    <div className="owner-truth">
      <div className="owner-truth__parts" role="tablist" aria-label="Truth">
        {TRUTH_PARTS.map((p) => (
          <button key={p.key} type="button" role="tab" aria-selected={part === p.key}
            className={part === p.key ? "owner-truth__part is-active" : "owner-truth__part"} onClick={() => setPart(p.key)}>{p.label}</button>
        ))}
      </div>
      {note ? <p className="owner-page__note">{note}</p> : null}

      {part === "analytics" ? (
        <section aria-label="Truth analytics">
          <div className="owner-truth__parts" role="group" aria-label="Group by">
            {TRUTH_BUCKETS.map((b) => (
              <button key={b.key} type="button" aria-pressed={bucket === b.key}
                className={bucket === b.key ? "owner-truth__part is-active" : "owner-truth__part"} onClick={() => setBucket(b.key)}>{b.label}</button>
            ))}
          </div>
          <p className="owner-detail__sub">Visitors are counted once a day each, from {ana?.visitsSince ? day(ana.visitsSince) : "today"}; no address is kept. A reading is one that billed at least a minute.</p>
          <div className="owner-stats">
            <div className="owner-stat"><strong>{ana ? ana.totals.visitors : "—"}</strong><span>Visitors</span></div>
            <div className="owner-stat"><strong>{ana ? ana.totals.signups : "—"}</strong><span>Sign-ups (new accounts that came to Truth)</span></div>
            <div className="owner-stat"><strong>{ana ? ana.totals.chat + ana.totals.calls : "—"}</strong><span>Readings ({ana?.totals.chat ?? "—"} chat, {ana?.totals.calls ?? "—"} call)</span></div>
            <div className="owner-stat"><strong>{ana ? ana.totals.minutes : "—"}</strong><span>Minutes</span></div>
            <div className="owner-stat"><strong>{ana ? truthRate(ana.totals.firstReadings, ana.totals.visitors) : "—"}</strong><span>Visit to first reading ({ana?.totals.firstReadings ?? "—"} first readings)</span></div>
            <div className="owner-stat"><strong>{ana ? ana.totals.advisorsOnline : "—"}</strong><span>Advisors online now</span></div>
            <div className="owner-stat"><strong>{ana ? ana.totals.repeatClients : "—"}</strong><span>Repeat clients (of {ana?.totals.clients ?? "—"} who had a reading)</span></div>
            <div className="owner-stat"><strong>{ana ? ana.totals.amaraReadings : "—"}</strong><span>Amara's readings ({ana?.totals.amaraMinutes ?? "—"} minutes)</span></div>
          </div>
          {ana && ana.series.length ? (
            <div className="owner-table-wrap"><table className="owner-table">
              <thead><tr><th>{bucket === "day" ? "Day" : bucket === "week" ? "Week of" : "Month"}</th><th>Visitors</th><th>Sign-ups</th><th>Chat</th><th>Calls</th><th>Minutes</th><th>Amara</th><th>Amara minutes</th></tr></thead>
              <tbody>{ana.series.map((r) => (
                <tr key={r.period}><td>{day(r.period + "T12:00:00")}</td><td>{r.visitors}</td><td>{r.signups}</td><td>{r.chat}</td><td>{r.calls}</td><td>{r.minutes}</td><td>{r.amaraReadings}</td><td>{r.amaraMinutes}</td></tr>))}</tbody>
            </table></div>
          ) : ana ? <p className="owner-detail__sub">Nothing in this period.</p> : null}
        </section>
      ) : part === "financials" ? (
        <section aria-label="Truth financials">
          <p className="owner-detail__sub">Truth takes Stripe test money until it goes live, so these are test dollars.</p>
          <div className="owner-stats">
            <div className="owner-stat"><strong>{truthMoney(fin?.moneyIn.totalCents)}</strong><span>Money in ({truthMoney(fin?.moneyIn.cardCents)} on cards, {truthMoney(fin?.moneyIn.topUpCents)} in {fin?.moneyIn.topUps ?? "—"} balance top-ups)</span></div>
            <div className="owner-stat"><strong>{truthMoney(fin?.houseShareCents)}</strong><span>House share (half of every advisor reading, and all of Amara's)</span></div>
            <div className="owner-stat"><strong>{truthMoney(fin?.advisorShareCents)}</strong><span>Advisors' share</span></div>
            <div className="owner-stat"><strong>{truthMoney(fin?.amara.cents)}</strong><span>Amara's earnings ({fin?.amara.readings ?? "—"} readings)</span></div>
            <div className="owner-stat"><strong>{truthMoney(fin?.freeCredit.cents)}</strong><span>Free credit given ({fin?.freeCredit.grants ?? "—"} gifts)</span></div>
            <div className="owner-stat"><strong>{fin ? fin.disputes.n : "—"}</strong><span>Disputes ({truthMoney(fin?.disputes.cents)}{fin?.disputes.unsent ? `, ${fin.disputes.unsent} waiting on you in the Truth console` : ""})</span></div>
          </div>
          <h3 className="owner-truth__h">Advisor payouts</h3>
          <div className="owner-stats">
            <div className="owner-stat"><strong>{truthMoney(fin?.payouts.pending.cents)}</strong><span>Pending ({fin?.payouts.pending.n ?? "—"})</span></div>
            <div className="owner-stat"><strong>{truthMoney(fin?.payouts.sent.cents)}</strong><span>Sent ({fin?.payouts.sent.n ?? "—"})</span></div>
            <div className="owner-stat"><strong>{truthMoney(fin?.payouts.cancelled.cents)}</strong><span>Canceled ({fin?.payouts.cancelled.n ?? "—"})</span></div>
          </div>
          {fin && fin.payouts.rows.length ? (
            <div className="owner-table-wrap"><table className="owner-table">
              <thead><tr><th>Reading</th><th>Advisor</th><th>Amount</th><th>Status</th><th>When</th><th></th></tr></thead>
              <tbody>{fin.payouts.rows.map((p) => (
                <tr key={String(p.sessionId)}>
                  <td>#{p.sessionId}</td><td>{p.advisor || "—"}</td><td>{truthMoney(p.cents)}</td><td>{payoutState(p)}</td>
                  <td>{payoutState(p) === "Pending" ? `available ${day(p.availableAt)}` : payoutState(p) === "Sent" ? day(p.sentAt) : payoutState(p) === "Reversed" ? day(p.reversedAt) : day(p.cancelledAt)}</td>
                  <td>{payoutState(p) === "Pending"
                    ? <button type="button" className="owner-link" disabled={busy === String(p.sessionId)} onClick={() => { void cancel(p); }}>Cancel payout</button>
                    : null}</td>
                </tr>))}</tbody>
            </table></div>
          ) : fin ? <p className="owner-detail__sub">No advisor payouts in this period.</p> : null}
        </section>
      ) : part === "gifts" ? (
        <section aria-label="Give free minutes">
          <p className="owner-detail__sub">Free credit lands on her balance at once and shows in her History as a gift. Advisors still earn their full half of any reading it pays for; the house covers it.</p>
          <form className="owner-truth__gift" onSubmit={(e) => { e.preventDefault(); void search(); }}>
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search an account by email or name" aria-label="Search an account" />
            <button type="submit" className="owner-truth__part">Search</button>
          </form>
          {found ? (found.length ? (
            <div className="owner-table-wrap"><table className="owner-table">
              <thead><tr><th></th><th>Account</th><th>Name</th><th>Balance</th></tr></thead>
              <tbody>{found.map((a) => (
                <tr key={a.id}><td><input type="radio" name="gift-who" checked={who?.id === a.id} onChange={() => { setWho(a); setRef(newRef()); }} aria-label={`Choose ${a.email}`} /></td>
                  <td>{a.email}</td><td>{a.name}</td><td>{truthMoney(a.balanceCents)}</td></tr>))}</tbody>
            </table></div>
          ) : <p className="owner-detail__sub">No account matches that.</p>) : null}
          {who ? (
            <div className="owner-truth__gift">
              <label>Amount for {who.email}<input type="text" inputMode="decimal" value={amount} onChange={(e) => { setAmount(e.target.value); setRef(newRef()); }} placeholder="$10.00" /></label>
              {cents ? (
                <table className="owner-table"><thead><tr><th>At</th><th>Minutes</th></tr></thead>
                  <tbody>{rates.map((r) => <tr key={r}><td>{truthMoney(r)} a minute</td><td>{giftMinutes(cents, r)}</td></tr>)}</tbody></table>
              ) : null}
              <label>Note (kept with the gift)<input type="text" value={giftWhy} onChange={(e) => setGiftWhy(e.target.value)} maxLength={300} placeholder="Why this gift" /></label>
              <button type="button" className="owner-truth__part is-active" disabled={!cents || busy === "gift"} onClick={() => { void give(); }}>Give {cents ? truthMoney(cents) : ""} free credit</button>
            </div>
          ) : null}
          <h3 className="owner-truth__h">Gifts given</h3>
          {gifts && gifts.length ? (
            <div className="owner-table-wrap"><table className="owner-table">
              <thead><tr><th>When</th><th>Account</th><th>Amount</th><th>By</th><th>Note</th></tr></thead>
              <tbody>{gifts.map((g) => (<tr key={g.id}><td>{day(g.createdAt)}</td><td>{g.email}</td><td>{truthMoney(g.cents)}</td><td>{g.by}</td><td>{g.note}</td></tr>))}</tbody>
            </table></div>
          ) : gifts ? <p className="owner-detail__sub">No gifts yet.</p> : null}
        </section>
      ) : (
        <section aria-label="Truth approvals">
          <p className="owner-detail__sub">Every advisor application, mirrored here. Approve, Re-send and Decline stay in the Truth console.</p>
          <a className="owner-link" href={TRUTH_APPLICATIONS_OPEN}>Open Advisor Applications in the Truth console</a>
          {/* THE OWNER'S DOOR (her order, 2026-10-03 night): both of her Truth owner pages one tap from this tab; each has
              the "12th House Dashboard" button back. */}
          <a className="owner-link" href="/api/truth-door?to=%2Fconsole%2Famara">Open Manage Amara</a>
          {apps ? (
            <>
              <div className="owner-stats">
                <div className="owner-stat"><strong>{apps.applications.filter((a) => a.status === "new").length}</strong><span>Waiting on you</span></div>
                <div className="owner-stat"><strong>{apps.applications.filter((a) => a.inviteSentAt && !a.inviteUsedAt).length}</strong><span>Invite codes sent, not used yet</span></div>
                <div className="owner-stat"><strong>{apps.applications.filter((a) => a.inviteUsedAt).length}</strong><span>Joined with their code</span></div>
              </div>
              <div className="owner-table-wrap"><table className="owner-table">
                <thead><tr><th>Applicant</th><th>Email</th><th>Applied</th><th>Status</th><th>Invite code</th></tr></thead>
                <tbody>{apps.applications.map((a) => (
                  <tr key={String(a.id)}>
                    <td>{a.name}</td><td>{a.email}</td><td>{day(a.createdAt)}</td><td>{a.status}</td>
                    <td>{a.inviteUsedAt ? `used ${day(a.inviteUsedAt)}` : a.inviteSentAt ? `sent ${day(a.inviteSentAt)}` : "not sent"}</td>
                  </tr>))}</tbody>
              </table></div>
            </>
          ) : null}
        </section>
      )}
    </div>
  );
}
